import { Component, Listen, Prop, h, Element } from '@stencil/core';
import '@material/web/list/list-item';
import { OmkList } from '../omk-list/omk-list';

/** OmkListItem is a container for items within a list. Avails 4 slots for content: start, end, headline and supporting-text. All slots are optional and can be combined as needed.
 * @slot start - This slot displays the slotted content to the left of the headline and supporting text.
 * @slot end - This slot displays the slotted content to the right of the headline and supporting text, at the end of the container.
 * @slot headline - This slot displays the headline for the list item. 
 * @slot supporting-text - This slot display the supporting text for the list item. */
@Component({
  tag: 'omk-list-item',
  styleUrl: 'omk-list-item.scss',
  shadow: { delegatesFocus: true },
})
export class OmkListItem {
  /** If true, the list item is displayed as disabled. */
  @Prop() disabled: boolean = false;
  /** Type of list item. Values can be "text" or "button". Defaults to "text". Type="button" makes the whole item clickable and the resulting click event fires when a) the omk-list-item is clicked and b) when a clickable component (like a checkbox) slotted in the start slot is clicked. */
  @Prop() type: 'text' | 'button' = 'text';
  /** Used when there are 2 headers in a list item. Primary header displays at the first header in this instance and the headline slot will assume the secondary header. */
  @Prop() primaryHeader = ''
  /** The list item's surface is clickable. By default, clicks on the list item will be redirected to the component slotted in the start slot. Assigning the value 'end' with redirect the list item's surface clicks to the component slotted in the end slot. Assigning the value 'none' will not redirect the click on the list item and will not show a pointer over the headline and supporting text. */
  @Prop({reflect: true}) redirectClicksToSlot: 'start' | 'end' | 'none' = 'start';
  /** Applies only when the redirectClicksToSlot prop is set to 'start' or to 'end'. Set this prop to true to include the surface of the other slot ('end' slot if redirectClicksToSlot="start" or 'start' slot if redirectClicksToSlot="end") as the surface used to direct clicks to the slot specified in the redirectClicksToSlot prop. */
  @Prop() redirectedClicksIncludeOtherSlot: boolean = false;

  @Element() el: HTMLElement;

  startSlotElement: HTMLSlotElement;
  endSlotElement: HTMLSlotElement;
  @Listen('click')
  handleClick(event: Event) {
    let slottedElement: HTMLSlotElement;
    switch(this.redirectClicksToSlot) { 
      case 'start': { 
        slottedElement = this.startSlotElement;
        this.clickElement(slottedElement, event);
        break; 
      } 
      case 'end': { 
        slottedElement = this.endSlotElement;
        this.clickElement(slottedElement, event);
        break; 
      } 
      default: { 
         break; 
      } 
    }
  }

  clickElement(slottedElement: HTMLSlotElement, event: Event) {
    const isIgnoredSlot = this.redirectedClicksIncludeOtherSlot ? event.composedPath().includes(slottedElement) : event.composedPath().includes(this.startSlotElement) || event.composedPath().includes(this.endSlotElement);
    if (slottedElement && !(isIgnoredSlot)) {
      (slottedElement.assignedElements()?.[0] as HTMLElement)?.click();
      setTimeout(() => (this.el.parentElement as unknown as OmkList).validate(true));
      event.stopImmediatePropagation();
    }
  }
  
  render() {
    return (
      <md-list-item disabled={this.disabled} type={this.type} class={{ 'hasPrimaryHeader': this.primaryHeader }}>
        <slot slot="start" name="start" ref={(el: HTMLSlotElement) => (this.startSlotElement = el)}></slot>
        <slot slot="end" name="end"  ref={(el: HTMLSlotElement) => (this.endSlotElement = el)}></slot>
        {this.primaryHeader && <span class="primary-header">{this.primaryHeader}</span>}
        <slot redirect-clicks-to-slot={this.redirectClicksToSlot} slot="headline" name="headline"></slot>
        <slot redirect-clicks-to-slot={this.redirectClicksToSlot} slot="supporting-text" name="supporting-text"></slot>
      </md-list-item>
    );
  }

}
